
import { Component, Prop, Vue } from 'vue-property-decorator'
// pdfjsのインポート
import * as pdfjsLib from 'pdfjs-dist'

/**
 * PDFコンテンツ表示
 */
@Component
export default class PdfContent extends Vue {
  /** PDFデータ */
  @Prop()
  private pdfData!: ArrayBuffer

  /** ビューポートのスケール指定 */
  private readonly SCALE_VIEWPORT = 1.5

  /**
   * 出力スケール(高DPIディスプレイ対応用処理)
   *
   * @returns 出力スケール
   */
  private get outputScale(): number {
    return window.devicePixelRatio || 1
  }

  /**
   * キャンバスの変換情報(高DPIディスプレイ対応用処理)
   *
   * @returns キャンバス用変換情報
   */
  private get transform(): number[] | undefined {
    // 出力スケールが等倍の場合は変換を行わない
    if (this.outputScale === 1) {
      return undefined
    }

    // 等倍以外の変換情報
    return [this.outputScale, 0, 0, this.outputScale, 0, 0]
  }

  /**
   * 読み込み時処理
   */
  private async mounted(): Promise<void> {
    // pdfjsの初期設定実行
    await this.initPdfJs()

    // 親コンポーネントに完了イベントを通知する
    this.$emit('completed')
  }

  /**
   * pdfjsの初期設定
   */
  private async initPdfJs(): Promise<void> {
    // ワーカー設定
    pdfjsLib.GlobalWorkerOptions.workerPort = new Worker(new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url))

    // CMAP設定
    const CMAP_URL = 'pdfjs-dist/cmaps/'

    // ドキュメントの読み込み
    const loadingTask = pdfjsLib.getDocument({
      data: this.pdfData,
      cMapUrl: CMAP_URL,
      // ログレベルの指定
      verbosity: pdfjsLib.VerbosityLevel.ERRORS,
    })
    const pdfDocument = await loadingTask.promise

    // ここで表示処理を実行
    await this.showPdfDocument(pdfDocument)
  }

  /**
   * PDFの表示処理
   *
   * @param pdfDocument 読み込み完了したPDFデータ
   */
  private async showPdfDocument(pdfDocument: pdfjsLib.PDFDocumentProxy): Promise<void> {
    // コンポーネントのルート要素取得
    const canvasContainer = this.$el as HTMLDivElement

    // ページ数を取得
    const numPages = pdfDocument.numPages

    // 全ページを一括描画
    // FIXME: 可能ならスクロール時などにlazyloadする方がベター
    for (let i = 0; i < numPages; i++) {
      // 対象ページの取得(ページ番号は1始まりなのでカウンタを+1して使用)
      const page = await pdfDocument.getPage(i + 1)

      // 1ページ分をレンダリング
      const canvas = await this.renderPdfPage(page)
      if (!canvas) {
        // 親コンポーネントにレンダリング失敗イベントを通知する
        this.$emit('failed')
        return
      }

      // canvasを画面に追加
      canvasContainer.appendChild(canvas)
    }
  }

  /**
   * PDFデータを1ページ分レンダリング
   *
   * @param pdfPage PDFページデータ
   * @returns 内容が描画されたcanvas
   */
  private async renderPdfPage(pdfPage: pdfjsLib.PDFPageProxy): Promise<HTMLCanvasElement | null> {
    // canvasと描画コンテキストの取得
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    // 描画コンテキストの生成失敗時用処理
    if (!context) {
      return null
    }

    // ビューポートの取得
    const viewport = pdfPage.getViewport({ scale: this.SCALE_VIEWPORT })

    // キャンバスサイズ計算
    canvas.width = Math.floor(viewport.width * this.outputScale)
    canvas.height = Math.floor(viewport.height * this.outputScale)
    canvas.style.width = Math.floor(viewport.width) + 'px'
    canvas.style.height = Math.floor(viewport.height) + 'px'

    // キャンバスにクラス情報付与
    canvas.className = 'pdf-content__canvas'

    // レンダリング処理
    const renderContext = {
      canvasContext: context,
      transform: this.transform,
      viewport: viewport,
    }
    pdfPage.render(renderContext)

    return canvas
  }
}
